import React from 'react';
import { Chip } from '@material-ui/core';
import { useGlobal } from '@rcg/ui-react';
import { formatPrice } from '@rcg/pricing-format';
import useStyles from './useStyles';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import FareCard from '../FareCard';
import getBrand from '../../utils/getBrand';

const STRIPE_COLORS = {
  royal: '#8AB5EF',
  celebrity: '#85D4F9',
};

const CABIN_ORDER = [
  {
    code: 'Y',
    nameKey: 'airClassYName',
    defaultName: 'Economy',
    shortNameKey: 'airClassYNameShort',
    shortDefaultName: 'Economy',
  },
  {
    code: 'S',
    nameKey: 'airClassSName',
    defaultName: 'Premium Economy',
    shortNameKey: 'airClassSNameShort',
    shortDefaultName: 'Prem. Econ.',
  },
  {
    code: 'C',
    nameKey: 'airClassCName',
    defaultName: 'Business',
    shortNameKey: 'airClassCNameShort',
    shortDefaultName: 'Business',
  },
  {
    code: 'F',
    nameKey: 'airClassFName',
    defaultName: 'First Class',
    shortNameKey: 'airClassFNameShort',
    shortDefaultName: 'First Class',
  },
];

const brand = getBrand();
const stripeColor = STRIPE_COLORS[brand.name] || STRIPE_COLORS.royal;
const isCelebrity = brand.name === 'celebrity';

const getLowestFare = (fares) =>
  fares.reduce(
    (min, fare) => (fare.pricePerPassenger.value < min.pricePerPassenger.value ? fare : min),
    fares[0],
  );

const hasOnSaleFare = (fares) =>
  (fares || []).some((fare) => Boolean(fare.wasPricePerPassenger?.value));

const CabinTabSelector = ({
  cabinGroups,
  selectedCode,
  selectedHashKey,
  onCabinSelect,
  onFareSelect,
  flight,
}) => {
  const classes = useStyles();
  const { renderContent } = useGlobal();
  const getDictionary = useDictionary(Dictionary);

  const selectedGroup = cabinGroups?.find((g) => g.cabinClass.code === selectedCode);

  return (
    <div className={classes.container}>
      <p className={classes.label}>
        {renderContent(
          Dictionary.airChooseCabinTitle.key,
          Dictionary.airChooseCabinTitle.defaultValue,
        ) || Dictionary.airChooseCabinTitle.defaultValue}
      </p>
      <div className={classes.tabRow}>
        {CABIN_ORDER.map(({ code, nameKey, defaultName, shortNameKey, shortDefaultName }) => {
          const group = (cabinGroups || []).find((g) => g.cabinClass.code === code);
          const isAvailable = !!group;
          const lowestFare = isAvailable && group.fares?.length ? getLowestFare(group.fares) : null;
          const isOnSale = isAvailable && hasOnSaleFare(group.fares);
          const isSelected = selectedCode === code;
          const tabStripeColor = isAvailable ? stripeColor : null;

          return (
            <button
              key={code}
              type="button"
              data-testid={`cabin-tab-${code}`}
              disabled={!isAvailable}
              className={`${classes.tab}${isSelected ? ` ${classes.tabSelected}` : ''}${
                !isAvailable ? ` ${classes.tabUnavailable}` : ''
              }`}
              onClick={isAvailable ? () => onCabinSelect(isSelected ? null : code) : undefined}
              aria-expanded={isSelected}
              aria-controls={`fare-section-${code}`}>
              <div
                className={`${classes.colorStripe}${
                  !isAvailable ? ` ${classes.stripeUnavailable}` : ''
                }`}
                style={tabStripeColor ? { backgroundColor: tabStripeColor } : undefined}
              />
              {isAvailable ? (
                <div className={classes.tabContent}>
                  <div className={classes.tabInfo}>
                    <span className={classes.cabinName}>
                      {renderContent(
                        Dictionary[nameKey]?.key || code,
                        Dictionary[nameKey]?.defaultValue || defaultName,
                      ) ||
                        Dictionary[nameKey]?.defaultValue ||
                        defaultName}
                    </span>
                    <span className={classes.cabinNameShort}>
                      {renderContent(
                        Dictionary[shortNameKey]?.key || code,
                        Dictionary[shortNameKey]?.defaultValue || shortDefaultName,
                      ) ||
                        Dictionary[shortNameKey]?.defaultValue ||
                        shortDefaultName}
                    </span>
                    {lowestFare && (
                      <div className={classes.fromRow}>
                        <span className={classes.fromLabel}>
                          {getDictionary(Dictionary.airFromPriceLabel.key)}
                        </span>
                        <span className={classes.lowestPrice}>
                          {formatPrice(
                            lowestFare.pricePerPassenger.value,
                            lowestFare.pricePerPassenger.currency,
                            true,
                            true,
                            false,
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {isOnSale && (
                    <Chip
                      label={getDictionary(Dictionary.airOnSaleLabel.key)}
                      className={isCelebrity ? classes.onSaleChipCelebrity : classes.onSaleChip}
                      size="small"
                    />
                  )}
                </div>
              ) : (
                <span className={classes.notAvailableLabel}>
                  {getDictionary(Dictionary.airCabinNotAvailableLabel.key)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedGroup && (
        <div
          id={`fare-section-${selectedCode}`}
          data-testid="fare-section"
          className={classes.fareSection}>
          <p className={classes.chooseFareLabel}>
            {getDictionary(Dictionary.airChooseFareTitle.key)}
          </p>
          {(selectedGroup.fares || []).map((fare) => (
            <FareCard
              key={fare.hashKey}
              fare={fare}
              isSelected={selectedHashKey === fare.hashKey}
              onSelect={() => onFareSelect(flight, fare)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CabinTabSelector;
